import { normalizeClawMediaType, parseYoloSegmentPayload, pickString, type SessionEventBase } from '../../_utils';
import type { ChatMessageItem } from '../../types';
import { isAssistantPlaceholder } from './helpers';
import type { EventHandlerCtx } from './types';

const buildMessageFromEvent = (event: SessionEventBase): ChatMessageItem | null => {
  const payload = event.payload ?? {};
  const createdAt = new Date(event.timestamp ?? Date.now()).toISOString();
  const seqSuffix = event.stream_seq ?? Date.now();

  if (event.type === 'conversation.message.created') {
    const content = pickString(payload.content);
    if (!content) return null;
    const messageId = pickString(payload.message_id);
    const story = parseYoloSegmentPayload(content);
    return {
      id: messageId ? `assistant-${messageId}` : `assistant-msg-${seqSuffix}`,
      serverMessageId: messageId || undefined,
      streamSeqs: event.stream_seq === undefined ? undefined : [String(event.stream_seq)],
      shareMessageIds: messageId ? [messageId] : undefined,
      role: 'assistant',
      content: story ? '' : content,
      story: story ?? undefined,
      thinkingLoading: false,
      createdAt,
    };
  }

  if (event.type === 'conversation.artifact.created') {
    // yolo 路径下 artifact 把媒体信息放在 payload.contents[0]，不是顶层 object_key/media_type。
    const first = Array.isArray(payload.contents)
      ? (payload.contents[0] as Record<string, unknown> | undefined)
      : undefined;
    const objectKey = pickString(first?.object_key) || pickString(first?.media_path);
    if (!objectKey) return null;
    const messageId = pickString(payload.message_id);
    return {
      id: messageId ? `assistant-artifact-${messageId}` : `assistant-artifact-${seqSuffix}`,
      serverMessageId: messageId || undefined,
      streamSeqs: event.stream_seq === undefined ? undefined : [String(event.stream_seq)],
      shareMessageIds: messageId ? [messageId] : undefined,
      role: 'assistant',
      content: '',
      fileList: [{ type: normalizeClawMediaType(pickString(first?.media_type)), content: objectKey }],
      thinkingLoading: false,
      createdAt,
    };
  }

  return null;
};

/**
 * yolo 路径：每条事件即一条独立 assistant 消息，开始即结束。
 * 后端不下发 turn.completed，每条事件本身就是终态：
 * - 第一条事件命中时替换 startChatStream 起的 thinking placeholder
 * - 每条都立即 markStreamCompleted，使 ref 清空、isStreaming 落下
 * - 后续事件 ref 已空，直接 push 成新消息
 */
export function handleYoloEvent(event: SessionEventBase, ctx: EventHandlerCtx) {
  const incoming = buildMessageFromEvent(event);
  if (!incoming) return;

  const { setMessages, assistantMessageIdRef, markStreamCompleted } = ctx;

  // ⚠️ 必须在 setMessages 前先把 ref 抓到局部。setMessages(updater) 是 batched，updater
  // 实际执行晚于本函数底部的 markStreamCompleted（清 ref），updater 内再读 ref 会拿到空串。
  const placeholderIdToReplace = assistantMessageIdRef.current;

  setMessages((prev) => {
    if (prev.some((m) => m.id === incoming.id)) return prev;
    const next = [...prev];
    const placeholderIdx = placeholderIdToReplace
      ? next.findIndex((m) => m.id === placeholderIdToReplace && isAssistantPlaceholder(m))
      : -1;
    if (placeholderIdx >= 0) next.splice(placeholderIdx, 1);
    next.push(incoming);
    return next;
  });

  markStreamCompleted();
}
